<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisModal from 'dashboard/components-next/LaisModal.vue';

defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'start']);

const flowOptions = Object.freeze([
  {
    key: 'schedule',
    label: 'Agendar nova visita',
    description: 'Use para criar uma visita futura com horário e confirmação.',
    icon: 'i-lucide-calendar-plus',
    primary: true,
  },
  {
    key: 'register',
    label: 'Registrar visita realizada',
    description:
      'Use para registrar presença, horário e resultado de uma visita passada.',
    icon: 'i-lucide-clipboard-check',
    primary: false,
  },
]);

const flowStages = Object.freeze([
  {
    key: 'visitor',
    label: 'Visitante',
    description:
      'Busca por nome na carteira e clientes fora da sua carteira por e-mail ou telefone.',
    icon: 'i-lucide-user-round',
  },
  {
    key: 'house',
    label: 'Imóvel',
    description:
      'Carteira, imóveis na casa e materialização nativa antes do agendamento.',
    icon: 'i-lucide-home',
  },
  {
    key: 'datetime',
    label: 'Data e hora',
    description:
      'Horários disponíveis, bloqueios, confirmação requerida e visita próxima.',
    icon: 'i-lucide-calendar-clock',
  },
  {
    key: 'details',
    label: 'Detalhes',
    description:
      'Status, pré-confirmação, observações e feedback quando for registro.',
    icon: 'i-lucide-list-checks',
  },
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    title="Nova visita"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[14px] leading-relaxed text-lais-text-body"
      >
        Você deseja agendar uma nova visita ou registrar uma visita que já
        ocorreu?
      </div>
      <div class="grid gap-3 sm:grid-cols-2">
        <button
          v-for="option in flowOptions"
          :key="option.key"
          type="button"
          class="rounded-lais-sm border px-4 py-4 text-left transition-colors"
          :class="
            option.primary
              ? 'border-lais-primary bg-lais-primary-50 text-lais-text hover:bg-lais-primary hover:text-white'
              : 'border-lais-border bg-white text-lais-text hover:border-lais-primary hover:text-lais-primary'
          "
          @click="emit('start', option.key)"
        >
          <span :class="option.icon" class="mb-3 block size-5" />
          <span class="block text-[14px] font-semibold">{{
            option.label
          }}</span>
          <span class="mt-1 block text-[12px] leading-relaxed">
            {{ option.description }}
          </span>
        </button>
      </div>

      <section
        class="rounded-lais-sm border border-lais-border bg-white px-4 py-4"
      >
        <h3 class="text-[14px] font-semibold text-lais-text">
          Etapas do CreateVisit
        </h3>
        <div class="mt-3 grid gap-3">
          <div
            v-for="(stage, index) in flowStages"
            :key="stage.key"
            class="grid grid-cols-[32px_minmax(0,1fr)] gap-3"
          >
            <span
              class="flex size-8 items-center justify-center rounded-full border border-lais-border bg-lais-bg-soft text-[12px] font-semibold text-lais-primary"
            >
              <span :class="stage.icon" class="size-4" aria-hidden="true" />
              <span class="sr-only">{{ index + 1 }}</span>
            </span>
            <span class="min-w-0">
              <span class="block text-[13px] font-semibold text-lais-text">
                {{ index + 1 }}. {{ stage.label }}
              </span>
              <span
                class="mt-0.5 block text-[12px] leading-relaxed text-lais-text-muted"
              >
                {{ stage.description }}
              </span>
            </span>
          </div>
        </div>
      </section>

      <div class="grid gap-2">
        <div
          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
        >
          <strong class="block text-amber-950"> Confirmação requerida </strong>
          Quando o horário estiver muito próximo, o agendamento deve nascer
          confirmado e o corretor precisa garantir que consegue realizar a
          visita.
        </div>
        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
        >
          <strong class="block text-lais-text">
            Fechamento com confirmação
          </strong>
          Ao sair de um agendamento ou registro preenchido, todas as informações
          inseridas serão perdidas se o corretor confirmar a saída.
        </div>
        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
        >
          <strong class="block text-lais-text">
            Cancelamento automático
          </strong>
          Visitas que aguardam confirmação continuam marcadas para validação do
          responsável e seguem a mesma regra de cancelamento automático do fluxo
          de referência.
        </div>
      </div>
    </div>
  </LaisModal>
</template>
